import { useEffect, useMemo, useRef, useState } from "react";
import { Check, PhoneCall, Send, Sparkles, X } from "lucide-react";
import { marked } from "marked";
import { api, ApiError } from "@/lib/api";
import { useSiteSettings } from "@/lib/cms";

// Configure marked once for AI chat responses:
// - breaks: render single newlines as <br> (matches the previous
//   whitespace-pre-wrap behaviour for plain text)
// - gfm: GitHub-flavoured markdown (better lists, autolinks)
marked.setOptions({ breaks: true, gfm: true });

type ChatMessage = {
  role: "user" | "assistant";
  content: string;
  /** Local-only error flag for failed sends */
  error?: boolean;
};

const GREETING_TEMPLATE = (brandShort: string): ChatMessage => ({
  role: "assistant",
  content: `Üdv! ${brandShort} asszisztens vagyok. Segítek megtalálni az Önnek leginkább megfelelő lakást — kérdezzen bátran árról, alaprajzról, tájolásról vagy bármi másról.\n\nVálasszon egy alábbi példa közül vagy írjon sajátot:`,
});

/**
 * Quick-pick example questions rendered as chips below the greeting
 * bubble. Disappear after the first user turn so the chat history
 * stays clean.
 */
const SUGGESTION_QUESTIONS = [
  "Milyen lakások elérhetők most?",
  "Mennyibe kerülnek a lakások?",
  "Mikor lesz az átadás?",
  "Milyen a műszaki tartalom?",
];

/**
 * Floating bottom-right chat widget. Opens a side panel and talks to
 * the /api/ai/chat endpoint backed by Anthropic Claude. Project + unit
 * facts are injected server-side as system context so the assistant
 * can answer specifically about the current inventory.
 */
export function AiChat() {
  const { brand, contact } = useSiteSettings();
  const privacyUrl = contact?.privacyPolicyUrl || "/o/adatvedelem";
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([
    GREETING_TEMPLATE(brand.shortName),
  ]);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  // Persisted server-side conversation id — sent back on every turn so the
  // full transcript is stored under one record for admin analytics.
  const [conversationId, setConversationId] = useState<string | null>(null);
  // Guiding quick-reply chips suggested by the assistant for the next turn.
  const [suggestions, setSuggestions] = useState<string[]>([]);

  // Inline lead-capture form ("Visszahívást kérek") state.
  const [showLead, setShowLead] = useState(false);
  const [lead, setLead] = useState({
    name: "",
    phone: "",
    email: "",
    message: "",
  });
  const [leadConsent, setLeadConsent] = useState(false);
  const [leadBusy, setLeadBusy] = useState(false);
  const [leadDone, setLeadDone] = useState(false);
  const [leadError, setLeadError] = useState<string | null>(null);

  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Re-seed greeting when brand finally loads (placeholder → real name)
  useEffect(() => {
    setMessages((prev) =>
      prev.length === 1 && prev[0].role === "assistant"
        ? [GREETING_TEMPLATE(brand.shortName)]
        : prev,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brand.shortName]);

  // Auto-scroll to newest message
  useEffect(() => {
    if (!open) return;
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, open, busy]);

  // Focus input when opening
  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  // Escape to close
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  async function send(textOverride?: string) {
    const text = (textOverride ?? draft).trim();
    if (!text || busy) return;
    const userMsg: ChatMessage = { role: "user", content: text };
    const nextMessages: ChatMessage[] = [...messages, userMsg];
    setMessages(nextMessages);
    setDraft("");
    setBusy(true);
    setSuggestions([]);
    try {
      // Only send the actual chat turns (not greeting metadata)
      const sendPayload = nextMessages
        .filter((m) => !m.error)
        .map((m) => ({ role: m.role, content: m.content }));

      const res = await api<{
        message: string;
        openLeadForm?: boolean;
        suggestions?: string[];
        conversationId?: string | null;
      }>("/api/ai/chat", {
        method: "POST",
        withAuth: false,
        body: {
          messages: sendPayload,
          conversationId,
          sourceUrl:
            typeof window !== "undefined" ? window.location.href : undefined,
        },
      });
      if (res.conversationId) setConversationId(res.conversationId);
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: res.message },
      ]);
      setSuggestions(Array.isArray(res.suggestions) ? res.suggestions : []);
      // Open the lead form ONLY when the visitor explicitly asked to be
      // contacted (the assistant confirms this via openLeadForm) — never on a
      // plain info/filter question.
      if (res.openLeadForm && !leadDone) {
        setLeadError(null);
        setShowLead(true);
      }
    } catch (err) {
      const msg =
        err instanceof ApiError &&
        typeof err.body === "object" &&
        err.body &&
        "message" in err.body
          ? String((err.body as { message: unknown }).message)
          : `Sajnos most nem érem el az AI asszisztenst. Próbálja újra, vagy hívja a ${brand.phone} számot.`;
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: msg, error: true },
      ]);
    } finally {
      setBusy(false);
    }
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      void send();
    }
  }

  async function submitLead() {
    const name = lead.name.trim();
    const phone = lead.phone.trim();
    const email = lead.email.trim();
    if (!name || (!phone && !email)) {
      setLeadError("Kérjük, adja meg a nevét és egy elérhetőséget (telefon vagy email).");
      return;
    }
    if (!leadConsent) {
      setLeadError("Kérjük, fogadja el az adatvédelmi tájékoztatót.");
      return;
    }
    setLeadBusy(true);
    setLeadError(null);
    try {
      await api("/api/ai/chat/lead", {
        method: "POST",
        withAuth: false,
        body: {
          conversationId,
          name,
          phone,
          email,
          message: lead.message.trim(),
          consent: true,
          sourceUrl:
            typeof window !== "undefined" ? window.location.href : undefined,
        },
      });
      setLeadDone(true);
      setShowLead(false);
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: `Köszönjük, ${name}! 🙌 Továbbítottuk a kollégánknak — hamarosan keressük a megadott elérhetőségen. Addig is kérdezzen bátran!`,
        },
      ]);
    } catch (err) {
      const msg =
        err instanceof ApiError &&
        typeof err.body === "object" &&
        err.body &&
        "error" in err.body
          ? "A küldés nem sikerült. Kérjük, próbálja újra, vagy hívjon minket."
          : `A küldés nem sikerült. Kérjük, hívja a ${brand.phone} számot.`;
      setLeadError(msg);
    } finally {
      setLeadBusy(false);
    }
  }

  return (
    <>
      {/* Floating launch button */}
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="fixed bottom-5 right-5 lg:bottom-8 lg:right-8 z-50 group flex items-center gap-2.5 bg-accent text-white pl-4 pr-5 py-3 rounded-full shadow-[0_6px_24px_rgba(0,0,0,0.25)] hover:bg-accent-700 transition-all hover:shadow-[0_8px_28px_rgba(0,0,0,0.35)] hover:-translate-y-0.5"
          aria-label="Kérdezz az AI asszisztenstől"
        >
          <Sparkles size={18} className="shrink-0" />
          <span className="text-xs font-semibold uppercase tracking-widish">
            Kérdezz az AI-tól
          </span>
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <>
          {/* Backdrop on mobile */}
          <div
            className="sm:hidden fixed inset-0 z-40 bg-black/50"
            onClick={() => setOpen(false)}
            aria-hidden
          />
          <div
            className="fixed inset-x-0 bottom-0 sm:inset-auto sm:bottom-5 sm:right-5 lg:bottom-8 lg:right-8 z-50 w-full sm:w-[400px] sm:max-w-[calc(100vw-2.5rem)] bg-white shadow-[0_12px_48px_rgba(0,0,0,0.25)] flex flex-col h-[80vh] sm:h-[600px] sm:max-h-[calc(100vh-3rem)]"
            role="dialog"
            aria-modal="true"
            aria-label="AI asszisztens"
          >
            {/* Header */}
            <header className="flex items-center justify-between gap-3 px-4 py-3 bg-brand-900 text-white">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 grid place-items-center bg-accent">
                  <Sparkles size={16} />
                </div>
                <div>
                  <div className="text-xs font-semibold uppercase tracking-widish">
                    {brand.shortName} asszisztens
                  </div>
                  <div className="text-[10px] text-white/60">
                    AI · 24/7 elérhető
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="text-white/70 hover:text-white p-1"
                aria-label="Bezár"
              >
                <X size={18} />
              </button>
            </header>

            {/* Messages */}
            <div
              ref={scrollRef}
              className="flex-1 overflow-y-auto p-4 bg-brand-50 space-y-3"
            >
              {messages.map((m, i) => (
                <MessageBubble key={i} message={m} />
              ))}
              {/* Quick-pick chips on the initial greeting so the visitor can
                  start with one tap. */}
              {messages.length === 1 && !busy && (
                <SuggestionChips
                  questions={SUGGESTION_QUESTIONS}
                  onPick={(q) => void send(q)}
                />
              )}
              {/* Guiding quick-reply chips the assistant suggested for the
                  next turn (shown after each answer). */}
              {messages.length > 1 &&
                !busy &&
                !showLead &&
                suggestions.length > 0 &&
                messages[messages.length - 1].role === "assistant" && (
                  <SuggestionChips
                    questions={suggestions}
                    onPick={(q) => void send(q)}
                  />
                )}
              {busy && (
                <div className="flex gap-2 items-center text-xs text-brand-500 px-3">
                  <span className="flex gap-1">
                    <span className="w-1.5 h-1.5 bg-accent rounded-full animate-pulse" />
                    <span
                      className="w-1.5 h-1.5 bg-accent rounded-full animate-pulse"
                      style={{ animationDelay: "0.2s" }}
                    />
                    <span
                      className="w-1.5 h-1.5 bg-accent rounded-full animate-pulse"
                      style={{ animationDelay: "0.4s" }}
                    />
                  </span>
                  <span>Gondolkodik...</span>
                </div>
              )}
            </div>

            {/* Inline lead-capture form ("Visszahívást kérek") — compact so it
                doesn't cover the conversation above it. */}
            {showLead && (
              <div className="border-t border-brand-200 bg-white px-3 py-2.5 space-y-1.5">
                <div className="flex items-center justify-between">
                  <div className="text-[13px] font-semibold text-brand-900">
                    Visszahívást kérek
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowLead(false)}
                    className="text-brand-400 hover:text-brand-700 p-0.5"
                    aria-label="Mégse"
                  >
                    <X size={15} />
                  </button>
                </div>
                <input
                  className="w-full border border-brand-200 px-2.5 py-1.5 text-[13px] rounded-md focus:outline-none focus:border-accent"
                  placeholder="Név *"
                  value={lead.name}
                  onChange={(e) =>
                    setLead((l) => ({ ...l, name: e.target.value }))
                  }
                />
                <div className="flex gap-1.5">
                  <input
                    className="flex-1 min-w-0 border border-brand-200 px-2.5 py-1.5 text-[13px] rounded-md focus:outline-none focus:border-accent"
                    placeholder="Telefon"
                    inputMode="tel"
                    value={lead.phone}
                    onChange={(e) =>
                      setLead((l) => ({ ...l, phone: e.target.value }))
                    }
                  />
                  <input
                    className="flex-1 min-w-0 border border-brand-200 px-2.5 py-1.5 text-[13px] rounded-md focus:outline-none focus:border-accent"
                    placeholder="Email"
                    inputMode="email"
                    value={lead.email}
                    onChange={(e) =>
                      setLead((l) => ({ ...l, email: e.target.value }))
                    }
                  />
                </div>
                <input
                  className="w-full border border-brand-200 px-2.5 py-1.5 text-[13px] rounded-md focus:outline-none focus:border-accent"
                  placeholder="Miben segíthetünk? (opcionális)"
                  value={lead.message}
                  onChange={(e) =>
                    setLead((l) => ({ ...l, message: e.target.value }))
                  }
                />
                <label className="flex items-start gap-2 text-[11px] leading-snug text-brand-600 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={leadConsent}
                    onChange={(e) => setLeadConsent(e.target.checked)}
                    className="mt-0.5 shrink-0 accent-accent"
                  />
                  <span>
                    Hozzájárulok, hogy megadott adataimat kapcsolatfelvétel
                    céljából kezeljék. Megismertem az{" "}
                    <a
                      href={privacyUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="text-accent underline hover:text-accent-700"
                    >
                      adatvédelmi tájékoztatót
                    </a>
                    . *
                  </span>
                </label>
                {leadError && (
                  <p className="text-[11px] text-red-600">{leadError}</p>
                )}
                <button
                  type="button"
                  onClick={() => void submitLead()}
                  disabled={leadBusy}
                  className="w-full bg-accent text-white text-[13px] font-semibold py-2 rounded-md hover:bg-accent-700 disabled:opacity-50 transition-colors"
                >
                  {leadBusy ? "Küldés..." : "Elküldöm"}
                </button>
                <p className="text-[10px] text-brand-400 text-center">
                  Név + telefon vagy email elég.
                </p>
              </div>
            )}

            {/* Input */}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                void send();
              }}
              className="border-t border-brand-200 p-3 bg-white"
            >
              {!showLead && (
                <button
                  type="button"
                  onClick={() => {
                    setLeadError(null);
                    setShowLead(true);
                  }}
                  className="w-full mb-2 flex items-center justify-center gap-1.5 text-xs font-semibold text-accent border border-accent/40 hover:bg-accent/5 py-2 rounded-md transition-colors"
                >
                  {leadDone ? (
                    <>
                      <Check size={14} /> Elküldve — új kérés
                    </>
                  ) : (
                    <>
                      <PhoneCall size={14} /> Visszahívást kérek
                    </>
                  )}
                </button>
              )}
              <div className="flex items-end gap-2">
                <textarea
                  ref={inputRef}
                  rows={1}
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={onKeyDown}
                  placeholder="Kérdezz egy lakásról, árról, hitelről..."
                  className="flex-1 resize-none border border-brand-200 px-3 py-2 text-sm focus:outline-none focus:border-accent rounded-md max-h-32 leading-snug"
                  disabled={busy}
                />
                <button
                  type="submit"
                  disabled={busy || !draft.trim()}
                  className="bg-accent text-white w-10 h-10 grid place-items-center rounded-md hover:bg-accent-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shrink-0"
                  aria-label="Küldés"
                >
                  <Send size={16} />
                </button>
              </div>
              <p className="text-[10px] text-brand-400 mt-1.5 px-1">
                Az AI tájékoztató jellegű információt ad. Foglaláshoz, hivatalos
                ajánlathoz keresse kollégánkat.
              </p>
            </form>
          </div>
        </>
      )}
    </>
  );
}

function SuggestionChips({
  questions,
  onPick,
}: {
  questions: readonly string[];
  onPick: (q: string) => void;
}) {
  return (
    <div className="flex flex-wrap gap-2 px-1">
      {questions.map((q) => (
        <button
          key={q}
          type="button"
          onClick={() => onPick(q)}
          className="text-left text-xs leading-snug bg-white border border-brand-200 text-brand-700 hover:border-accent hover:text-accent px-3 py-2 rounded-full transition-colors max-w-full"
        >
          {q}
        </button>
      ))}
    </div>
  );
}

function MessageBubble({ message }: { message: ChatMessage }) {
  const isUser = message.role === "user";

  // Only render markdown for successful AI replies. User input and
  // fallback error strings stay plain text (no surprise formatting,
  // no XSS surface from typed user input).
  const renderAsMarkdown = !isUser && !message.error;
  const html = useMemo(() => {
    if (!renderAsMarkdown) return null;
    try {
      return marked.parse(message.content, { async: false }) as string;
    } catch {
      return null;
    }
  }, [message.content, renderAsMarkdown]);

  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div
        className={`max-w-[85%] px-3 py-2 text-sm leading-relaxed ${
          isUser
            ? "bg-accent text-white whitespace-pre-wrap"
            : message.error
              ? "bg-red-50 text-red-800 border border-red-200 whitespace-pre-wrap"
              : "bg-white text-brand-700 border border-brand-200"
        }`}
      >
        {!isUser && (
          <div className="flex items-center gap-1.5 mb-1">
            <Sparkles size={11} className="text-accent" />
            <span className="text-[10px] uppercase tracking-widish font-semibold text-brand-400">
              AI
            </span>
          </div>
        )}
        {html != null ? (
          <div
            className="ai-chat-prose"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: html }}
          />
        ) : (
          message.content
        )}
      </div>
    </div>
  );
}
